import { streemLogger } from '../util/streem_logger';
import { invariant } from '@streem/toolbox';
export class RemoteAttendeeObserver {
    constructor(audioVideoFacade, remoteAttendeeManager, onRemoteVideoAvailableChange) {
        this.audioVideoFacade = audioVideoFacade;
        this.remoteAttendeeManager = remoteAttendeeManager;
        this.onRemoteVideoAvailableChange = onRemoteVideoAvailableChange;
        this.logger = streemLogger.extend('sdk-chime-video:RemoteAttendeeObserver');
        this.attendeeIdPresenceCallback = (attendeeId, present, externalUserId, dropped) => {
            if (this.remoteAttendeeManager.checkIfAttendeeCanBeIgnored(attendeeId, externalUserId)) {
                this.logger.debug('Ignoring attendee with userId', externalUserId);
                return;
            }
            invariant(externalUserId, 'External user ID is required');
            if (present) {
                streemLogger.info(`Remote attendee attendeeId=${attendeeId} joined room`);
                this.remoteAttendeeManager.getOrCreateAttendee(attendeeId, externalUserId);
            }
            else {
                streemLogger.info(`Remote attendee attendeeId=${attendeeId} left room`);
                this.remoteAttendeeManager.removeAttendee(attendeeId);
            }
            if (dropped) {
                streemLogger.info(`Remote attendee attendeeId=${attendeeId} was dropped. value of present=${present}`);
            }
        };
        this.audioVideoFacade.realtimeSubscribeToAttendeeIdPresence(this.attendeeIdPresenceCallback);
    }
    videoTileDidUpdate(tileState) {
        if (tileState.localTile) {
            return;
        }
        this.logger.info('remoteVideoTileDidUpdate: Remote video tile updating...', {
            ...tileState,
            boundVideoElement: !!tileState.boundVideoElement,
            boundVideoStream: !!tileState.boundVideoStream,
        });
        if (!tileState.boundAttendeeId || !tileState.boundExternalUserId) {
            this.logger.info(`remoteVideoTileDidUpdate: boundAttendeeId=${tileState.boundAttendeeId} || boundExternalUserId=${tileState.boundExternalUserId} is not defined`);
            return;
        }
        if (this.remoteAttendeeManager.checkIfAttendeeCanBeIgnored(tileState.boundAttendeeId, tileState.boundExternalUserId)) {
            this.logger.debug('Ignoring attendee with userId', tileState.boundExternalUserId);
            return;
        }
        const remoteAttendee = this.remoteAttendeeManager.getOrCreateAttendee(tileState.boundAttendeeId, tileState.boundExternalUserId);
        invariant(tileState.tileId, 'Tile ID is required');
        if (!tileState.boundVideoElement && !!tileState.boundVideoStream) {
            this.audioVideoFacade.bindVideoElement(tileState.tileId, remoteAttendee.videoTrack.videoTrackElement);
            this.logger.info(`Remote video tile bound for userId: ${remoteAttendee.externalUserId}`);
            // This is a weird friction point between the existing ITrackProvider interface that assumes 1 remote participant
            // and this code that is trying to support multiple remote participants
            this.remoteAttendeeManager.updateEnabledStateForAttendeeVideoTrack(remoteAttendee, true);
        }
        // this can happen when Chime detects there is insufficient bandwidth to receive video
        if (tileState.paused && !remoteAttendee.isPaused) {
            this.remoteAttendeeManager.pauseAttendee(remoteAttendee);
        }
        else if (!tileState.paused && remoteAttendee.isPaused) {
            this.remoteAttendeeManager.unpauseAttendee(remoteAttendee);
        }
    }
    videoTileWasRemoved(tileId) {
        const remoteAttendee = this.remoteAttendeeManager.getAttendeeByTileId(tileId);
        if (!remoteAttendee) {
            this.logger.info(`No remote attendee found for tileId: ${tileId}, tile was removed.`);
            return;
        }
        this.logger.info(`Remote video tile for tileId: ${tileId} unbound for userId: ${remoteAttendee.externalUserId}`);
        this.remoteAttendeeManager.removeTileIdFromAttendee(remoteAttendee);
        this.remoteAttendeeManager.updateEnabledStateForAttendeeVideoTrack(remoteAttendee, false);
        if (remoteAttendee.isPaused) {
            this.remoteAttendeeManager.unpauseAttendee(remoteAttendee);
        }
    }
    disconnect() {
        this.audioVideoFacade.realtimeUnsubscribeToAttendeeIdPresence(this.attendeeIdPresenceCallback);
    }
    videoAvailabilityDidChange(availablity) {
        this.onRemoteVideoAvailableChange(!!availablity.remoteVideoAvailable);
    }
}
//# sourceMappingURL=remote_attendee_observer.js.map